// Round trip for drafting outreach emails outside the app: export the leads,
// write a tailored email for each one elsewhere (each is researched for that
// business, so it can't be a template), then import the finished drafts back
// onto their leads for review and sending from the Outreach tab.

// ── Export ─────────────────────────────────────────────────────────────────────

function toExportLead(l) {
  return {
    id:           l._id,
    businessName: l.businessName,
    contactName:  l.contactName || '',
    email:        l.email || '',
    otherEmails:  l.otherEmails || [],
    phone:        l.phone || '',
    website:      l.website || '',
    socials:      (l.socials || []).map(({ platform, handle, url }) => ({ platform, handle, url })),
    location:     l.location || '',
    tags:         l.tags || [],
    status:       l.status,
    hasDraft:     !!l.draft,
    createdAt:    l.createdAt,
    updatedAt:    l.updatedAt,
    interactions: (l.interactions || []).map(({ _id, ...rest }) => rest),
  };
}

export function buildLeadsExport(leads, now = new Date()) {
  const out = leads.map(toExportLead);
  const byStatus = {};
  for (const l of out) byStatus[l.status] = (byStatus[l.status] || 0) + 1;
  return {
    exportedAt: now.toISOString(),
    source:     'farm-dashboard',
    summary:    { total: out.length, withEmail: out.filter(l => l.email).length, byStatus },
    leads:      out,
  };
}

export function leadsExportFilename(now = new Date()) {
  return `outreach-leads-${now.toISOString().slice(0, 10)}.json`;
}

// ── Import ─────────────────────────────────────────────────────────────────────
// Expected file shape: { drafts: [{ leadId, businessName, to, subject, html, notes }] }
// (a bare array of drafts is accepted too). `businessName` and `to` record
// what the draft was written for, so it can be checked against the lead as it
// is now: the lead list may have changed between export and import.

const norm = s => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

// Each row comes back with:
//   blocked  - can't be imported at all (bad entry, no matching lead)
//   include  - ticked by default; false for rows worth a second look
//   issues   - human-readable reasons, shown next to the row
export function parseDraftsFile(json, leads) {
  const list = Array.isArray(json) ? json : json?.drafts;
  if (!Array.isArray(list)) return { rows: [], error: 'This file has no "drafts" list in it' };
  if (!list.length)         return { rows: [], error: 'This file has no drafts in it' };

  const byId = new Map(leads.map(l => [l._id, l]));
  const seen = new Set();

  const rows = list.map(d => {
    const issues = [];
    const row = {
      leadId:       String(d?.leadId || ''),
      businessName: String(d?.businessName || ''),
      subject:      typeof d?.subject === 'string' ? d.subject.trim() : '',
      html:         typeof d?.html === 'string' ? d.html : '',
      notes:        typeof d?.notes === 'string' ? d.notes : '',
      to:           '',
      blocked:      false,
      include:      true,
      issues,
    };
    const block = msg => { issues.push(msg); row.blocked = true; row.include = false; };

    if (!row.subject || !row.html.trim()) { block('Missing a subject or message'); return row; }

    const lead = byId.get(row.leadId);
    if (!lead) { block('No matching lead (deleted since the export?)'); return row; }
    if (row.businessName && norm(row.businessName) !== norm(lead.businessName)) {
      block(`Written for "${row.businessName}" but this lead is now "${lead.businessName}"`);
      return row;
    }
    if (seen.has(row.leadId)) { block('Duplicate: this lead appears earlier in the file'); return row; }
    seen.add(row.leadId);

    row.businessName = lead.businessName;
    row.to = lead.email || '';

    if (lead.status !== 'new') {
      issues.push(`Already ${lead.status}`);
      row.include = false;
    }
    // A draft can carry an `email` found during research, for leads that had
    // none. It's only ever filled into an empty lead, never swapped for one
    // the lead already has.
    const found = typeof d.email === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())
      ? d.email.trim().toLowerCase() : '';
    if (!lead.email && found) {
      row.setEmail = found;
      row.to = found;
      issues.push(`Adds ${found} as this lead's email`);
    } else if (!lead.email) {
      issues.push('No email address on this lead yet');
      row.include = false;
    } else if (d.to && norm(d.to) !== norm(lead.email)) {
      issues.push(`Written for ${d.to}, primary email is now ${lead.email}`);
    }
    if (lead.draft) issues.push('Replaces the draft already on this lead');

    return row;
  });

  return { rows, error: null };
}
